import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  CheckCircle, Book, Sparkles, Calculator, 
  ArrowRight, Mail, Loader2, PartyPopper
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

const ebookData = {
  'rv-buying-guide': {
    title: 'The Complete RV Buying Guide',
    color: 'blue',
  },
  'rv-travel-guide': {
    title: 'The RV Travel & Camping Guide',
    color: 'green',
  }
};

const EbookSuccessPage = () => {
  const { ebookType } = useParams();
  const [searchParams] = useSearchParams();
  const dealerSlug = searchParams.get('dealer');
  
  const [loading, setLoading] = useState(true);
  const [sent, setSent] = useState(false);
  const [dealer, setDealer] = useState(null);

  const ebook = ebookData[ebookType] || ebookData['rv-buying-guide'];

  useEffect(() => {
    completeAndSendEbook();
    if (dealerSlug) {
      fetchDealer();
    }
  }, []);

  const fetchDealer = async () => {
    try {
      const res = await axios.get(`${API}/api/dealer/by-slug/${dealerSlug}`);
      setDealer(res.data);
    } catch (err) {
      console.error('Error fetching dealer:', err);
    }
  };

  const completeAndSendEbook = async () => {
    try {
      // Get stored lead data
      const leadData = sessionStorage.getItem('ebook_lead');
      if (leadData) {
        const data = JSON.parse(leadData);
        await axios.post(`${API}/api/ebook/complete-quiz?email=${encodeURIComponent(data.email)}&ebook_type=${ebookType}`);
        setSent(true);
        sessionStorage.removeItem('ebook_lead');
      }
    } catch (err) {
      console.error('Error completing ebook:', err);
    } finally {
      setLoading(false);
    }
  };

  const isBlue = ebook.color === 'blue';
  const gradientClass = isBlue ? 'from-blue-600 to-indigo-700' : 'from-green-600 to-emerald-700';
  const lightBgClass = isBlue ? 'bg-blue-50' : 'bg-green-50';
  const textColorClass = isBlue ? 'text-blue-600' : 'text-green-600';
  const buttonClass = isBlue ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="animate-spin text-blue-500 mx-auto mb-4" size={48} />
          <p className="text-gray-600">Preparing your free guide...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <div className={`bg-gradient-to-br ${gradientClass} text-white py-12 md:py-20`}>
        <div className="max-w-3xl mx-auto px-4 text-center">
          <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <PartyPopper size={40} />
          </div>
          <h1 className="text-3xl md:text-5xl font-bold mb-4">Congratulations!</h1>
          <p className="text-xl md:text-2xl opacity-90 mb-2">Your Free Guide is On Its Way!</p>
          <p className="opacity-80">Check your email for instant access</p>
        </div>
      </div>

      {/* What You Got */}
      <div className="max-w-4xl mx-auto px-4 py-12 md:py-16">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Here's What You Unlocked</h2>
          <p className="text-gray-600">All the tools you need to buy your perfect RV</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Ebook */}
          <div className={`${lightBgClass} rounded-2xl p-6 border-2 ${isBlue ? 'border-blue-200' : 'border-green-200'}`}>
            <div className="flex items-center gap-4 mb-4">
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${gradientClass} flex items-center justify-center`}>
                <Book className="text-white" size={24} />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">{ebook.title}</h3>
                <div className="flex items-center gap-1 text-green-600 text-sm">
                  <CheckCircle size={14} />
                  <span>Sent to your email</span>
                </div>
              </div>
            </div>
            <Link to={`/ebook-viewer/${ebookType}`}>
              <Button className={`w-full ${buttonClass}`}>
                Read Now
                <ArrowRight className="ml-2" size={16} />
              </Button>
            </Link>
          </div>

          {/* RV Matches */}
          <div className="bg-purple-50 rounded-2xl p-6 border-2 border-purple-200">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
                <Sparkles className="text-white" size={24} />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Your RV Matches</h3>
                <div className="flex items-center gap-1 text-green-600 text-sm">
                  <CheckCircle size={14} />
                  <span>Ready to view</span>
                </div>
              </div>
            </div>
            <Link to="/sign-in">
              <Button className="w-full bg-purple-600 hover:bg-purple-700">
                View Matches
                <ArrowRight className="ml-2" size={16} />
              </Button>
            </Link>
          </div>
        </div>

        {/* Cost Calculator Tool */}
        <div className="mt-8 bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-6 border-2 border-amber-200">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
              <Calculator className="text-white" size={24} />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Free RV Cost Calculator</h3>
              <div className="flex items-center gap-1 text-green-600 text-sm">
                <CheckCircle size={14} />
                <span>Unlocked for you</span>
              </div>
            </div>
          </div>

          <p className="text-gray-600 text-sm mb-4">
            Calculate the true cost of RV ownership including financing, insurance, maintenance, and more.
          </p>

          <Link to="/vip/cost-calculator" className="block">
            <Button className="w-full bg-amber-500 hover:bg-amber-600">
              Open Cost Calculator
              <ArrowRight className="ml-2" size={16} />
            </Button>
          </Link>
        </div>

        {/* Email Notice */}
        <div className="mt-8 flex items-center justify-center gap-3 text-gray-500">
          <Mail size={18} />
          <p className="text-sm">
            Didn't get the email? Check your spam folder or{' '}
            <button 
              onClick={completeAndSendEbook}
              className="text-blue-600 hover:underline"
            >
              resend it
            </button>
          </p>
        </div>
      </div>

      {/* Footer */}
      {dealer && (
        <div className="border-t border-gray-200 py-8">
          <div className="max-w-6xl mx-auto px-4 text-center">
            <p className="text-gray-500 text-sm">
              Brought to you by <strong className="text-gray-700">{dealer.business_name}</strong>
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default EbookSuccessPage;
